import { useCallback, useEffect, useState } from 'react';
import { View } from 'smoothie';

import { useDebouncedPasswordCheck } from '../hooks/useDebouncedPasswordCheck';
import type {
    CheckPasswordResponse,
    PasswordRequirementsFeedback,
} from '../types/Auth';
import { PasswordRequirements } from './PasswordRequirements';

interface PasswordStrengthCheckerProps {
    password: string;
    onValidationChange?: (isValid: boolean) => void;
    onStrengthCheckChange?: (response: CheckPasswordResponse) => void;
}

export const PasswordStrengthChecker = ({
    password,
    onValidationChange,
    onStrengthCheckChange,
}: PasswordStrengthCheckerProps) => {
    const [passwordFeedback, setPasswordFeedback] =
        useState<PasswordRequirementsFeedback | null>(null);
    const [validPassword, setValidPassword] = useState<boolean>(false);
    const [showPasswordRequirements, setShowPasswordRequirements] =
        useState(false);

    const onPasswordStrengthChange = useCallback(
        (response: CheckPasswordResponse) => {
            setPasswordFeedback(response?.strengthCheck ?? null);
            setValidPassword(response.validPassword);
            onStrengthCheckChange?.(response);
        },
        [onStrengthCheckChange],
    );

    useDebouncedPasswordCheck({
        watchedPassword: password,
        onChange: onPasswordStrengthChange,
    });

    useEffect(() => {
        if (password.length < 3) {
            setShowPasswordRequirements(false);
            setValidPassword(false);
            setPasswordFeedback(null);
        } else {
            setShowPasswordRequirements(!validPassword);
        }
    }, [password, validPassword]);

    useEffect(() => {
        onValidationChange?.(validPassword);
    }, [validPassword, onValidationChange]);

    return (
        <View
            marginHorizontal={4}
            marginTop={showPasswordRequirements ? 12 : 0}
            marginBottom={showPasswordRequirements ? 8 : 0}
            animation="quick"
            opacity={showPasswordRequirements ? 1 : 0}
            maxHeight={showPasswordRequirements ? 120 : 0}
            overflow="hidden"
            animateOnly={['opacity', 'maxHeight', 'marginTop', 'marginBottom']}
        >
            <PasswordRequirements
                validPassword={validPassword}
                passwordFeedback={passwordFeedback}
            />
        </View>
    );
};
